import React, { useEffect, Component } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { ThemeProvider } from './components/ThemeContext'
import { AuthProvider, useAuth } from './auth/AuthContext'
// MSAL interaction status is no longer used for gating UI
import Navbar from './components/Navbar'
import OrderForm from './components/OrderForm'
import RfpForm from './components/RfpForm'
import AffiliateCampaignForm from './components/AffiliateCampaignForm'
import AdminDashboard from './components/AdminDashboard'
import OrderStatus from './components/OrderStatus'
import Login from './components/Login'
import './App.css'
import VenueSelectionModal from './components/VenueSelectionModal'
import BrandRfpPortal from './components/BrandRfpPortal'
import PublishCampaignWizard from './components/io/PublishCampaignWizard'
import CampaignBuilder from './components/io/CampaignBuilder'
import CampaignList from './components/io/CampaignList'
import CampaignRecord from './components/io/CampaignRecord'
import PackagePage from './components/io/PackagePage'
import InventoryPage from './components/io/InventoryPage'
import AudienceCampaignWizard, { AudienceHome } from './components/io/AudienceCampaigns'

const brandRfpPortalUrl = (import.meta.env.VITE_BRAND_RFP_PORTAL_URL || '').trim()

// Loading component for authentication initialization
const AuthLoadingComponent = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="loading-spinner mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
          Initializing Authentication
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Please wait while we set up your session...
        </p>
      </div>
    </div>
  );
};

// Error boundary component for authentication errors
const AuthErrorComponent = ({ error, onRetry }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl text-red-600">⚠️</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
          Authentication Error
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {error || 'There was a problem with authentication. Please try again.'}
        </p>
        <div className="space-x-3">
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

// ENHANCED: Admin redirect component with AGGRESSIVE redirection
const AdminRedirectHandler = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search||'');
    const viewParam = params.get('view');
    const suppressRedirect =
      (typeof window !== 'undefined' && window.localStorage?.getItem('adv_view_as_app') === '1') ||
      location.pathname === '/app' ||
      viewParam === 'app';

    // If we're on /admin, clear the suppression flag so future navigations behave normally
    if (location.pathname.startsWith('/admin')) {
      try { window.localStorage?.removeItem('adv_view_as_app'); } catch {}
    }

    console.log('🔍 AdminRedirectHandler check:', { 
      loading, 
      isAuthenticated, 
      isAdmin, 
      pathname: location.pathname,
      inProgress: 'none',
      suppressRedirect
    });
    
    // SUPER AGGRESSIVE: Force admin redirect from main app entry points (/, /app),
    // but allow admins to visit other routes like /status/:orderId
    if (isAuthenticated && isAdmin && !loading && !suppressRedirect) {
      const shouldRedirectFrom =
        location.pathname === '/' ||
        location.pathname === '/app';

      if (shouldRedirectFrom && !location.pathname.startsWith('/admin')) {
        console.log('🚨 FORCING admin redirect from', location.pathname, 'to /admin');
        navigate('/admin', { replace: true });
        return;
      }
    }
  }, [isAuthenticated, isAdmin, loading, location.pathname, navigate]);

  // Force immediate redirect if admin lands on wrong page
  try {
    const params = new URLSearchParams(window.location.search||'');
    const viewParam = params.get('view');
    const path = window.location.pathname;
    const suppressRedirect = window.localStorage?.getItem('adv_view_as_app') === '1' ||
      path === '/app' || viewParam === 'app';
    const shouldRedirectFrom = path === '/' || path === '/app';
    if (isAuthenticated && isAdmin && !loading && shouldRedirectFrom && !path.startsWith('/admin') && !suppressRedirect) {
      navigate('/admin', { replace: true });
      return null;
    }
  } catch {}

  return null;
};

// Error boundary to catch OrderForm rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding: 32, background: 'red', color: 'white'}}>
          <h2>Error rendering OrderForm:</h2>
          <pre>{this.state.error?.toString()}</pre>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Role home: venues land on Publish; agencies on campaign list;
 * sales stay on RFP; admins pass through to /admin.
 */
function HomeEntry() {
  const { user, isAdmin, loading } = useAuth();
  if (loading) {
    return <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading…</div>;
  }
  const isVenue = user?.userType === 'venue' || user?.isVenue === true;
  const isAffiliate = user?.userType === 'affiliate' || user?.isAffiliate === true;
  if (isAffiliate) {
    return <CampaignList kind="guaranteed" />;
  }
  if (isVenue) {
    return <PublishCampaignWizard />;
  }
  if (isAdmin) {
    return <CampaignList />;
  }
  return <Navigate to="/rfp" replace />;
}

// FIXED: Enhanced App content with better authentication state handling
const AppContent = () => {
  const { loading, error, isAuthenticated, isAdmin, clearError } = useAuth();
  const location = useLocation();
  
  // Public routes that don't require authentication
  const publicRoutes = ['/login'];
  const isPublicRoute = publicRoutes.includes(location.pathname) || location.pathname.startsWith('/status/');
  
  // Show loading only while our AuthContext is initializing
  if (loading) {
    return <div style={{padding: 32, color: 'blue', fontWeight: 'bold'}}>Loading...</div>;
  }

  // Show error state if authentication failed (but allow public routes)
  if (error && !isPublicRoute) {
    return (
      <div style={{padding: 32, color: 'red', fontWeight: 'bold'}}>
        Authentication error: {error}
        <br/><button onClick={clearError}>Retry</button>
      </div>
    );
  }

  // Show main app content - Routes are always rendered so public routes like /login are accessible
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
      {isAuthenticated && <Navbar />}
      {isAuthenticated && <VenueSelectionModal />}
      {isAuthenticated && <AdminRedirectHandler />}
      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route
            path="/"
            element={
              <RequireServerAuthFixed>
                <HomeEntry />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/app"
            element={
              <RequireServerAuthFixed>
                <HomeEntry />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/order"
            element={
              <RequireServerAuthFixed>
                <ErrorBoundary>
                  <CampaignBuilder />
                </ErrorBoundary>
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/order/legacy"
            element={
              <RequireServerAuthFixed>
                <ErrorBoundary>
                  <OrderForm />
                </ErrorBoundary>
              </RequireServerAuthFixed>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <RequireServerAuthFixed>
                <AdminDashboard />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/rfp"
            element={
              <RequireServerAuthFixed>
                <RfpForm />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/affiliate"
            element={
              <RequireServerAuthFixed>
                <ErrorBoundary>
                  <CampaignBuilder />
                </ErrorBoundary>
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/affiliate/legacy"
            element={
              <RequireServerAuthFixed>
                <ErrorBoundary>
                  <AffiliateCampaignForm />
                </ErrorBoundary>
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/campaigns/venue/new"
            element={
              <RequireServerAuthFixed>
                <PublishCampaignWizard />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/campaigns"
            element={
              <RequireServerAuthFixed>
                <CampaignList />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/campaigns/new"
            element={
              <RequireServerAuthFixed>
                <CampaignBuilder />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/campaigns/:id"
            element={
              <RequireServerAuthFixed>
                <CampaignRecord />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/packages"
            element={
              <RequireServerAuthFixed>
                <PackagePage />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/inventory"
            element={
              <RequireServerAuthFixed>
                <InventoryPage />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/audience"
            element={
              <RequireServerAuthFixed>
                <AudienceHome />
              </RequireServerAuthFixed>
            }
          />
          <Route
            path="/audience/new"
            element={
              <RequireServerAuthFixed>
                <AudienceCampaignWizard />
              </RequireServerAuthFixed>
            }
          />
          <Route path="/status/:orderId" element={<OrderStatus />} />
          <Route path="*" element={
            <div className="text-center py-16">
              <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">404 - Page Not Found</h1>
              <p className="text-gray-600 dark:text-gray-300 mb-8">The page you're looking for doesn't exist.</p>
              <a href="/" className="btn-primary">Go Home</a>
            </div>
          } />
        </Routes>
      </main>
    </div>
  );
};

// Improved RequireServerAuth - trust AuthContext authentication state
// AuthContext already checks /api/me, so we don't need to duplicate that check
function RequireServerAuthFixed({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    // If AuthContext has finished loading and user is not authenticated, redirect to login
    if (!loading && !isAuthenticated) {
      const next = encodeURIComponent(loc.pathname + (loc.search || ''));
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, loc.pathname, loc.search]);

  // Show loading state while AuthContext is checking authentication
  if (loading) {
    return <div style={{padding: 32, color: 'purple', fontWeight: 'bold'}}>Checking authentication...</div>;
  }

  // If not authenticated, return null (redirect is in progress)
  if (!isAuthenticated) {
    return null;
  }

  // User is authenticated, render children
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App